import * as yup from 'yup';

import { FaultyDeviceRequestBody } from '@/types/payloads';
import {
	emptyToUndefined,
	FormLabels,
	maxLengthErrorMsg,
	safeAnnotationTextErrorMessage,
	safeAnnotationTextRegExp,
} from '@/utils';

export const textMaxLength = 2000;

export const faultReasonOptions = [
	{
		value: FaultyDeviceRequestBody.faultReason.UNREADABLE,
		label: 'Unreadable',
	},
	{
		value: FaultyDeviceRequestBody.faultReason.NOT_RECORDING,
		label: 'Not recording water volume correctly',
	},
	{
		value: FaultyDeviceRequestBody.faultReason.LEAKING_WATER,
		label: 'Leaking water or air from the device or its attachments',
	},
	{
		value: FaultyDeviceRequestBody.faultReason.BROKEN_SEALS,
		label: 'Broken or damaged tamper-evident seals',
	},
	{
		value: FaultyDeviceRequestBody.faultReason.NOT_TRANSMITTING,
		label: 'Not transmitting information',
	},
];

export interface DeviceFaultyFormValues {
	deviceID: string;
	faultIdentifiedDate: Date;
	faultReason: FaultyDeviceRequestBody.faultReason[];
	relevantAuthorisations?: string;
	hasOtherPersons: boolean;
	personDetails?: string;
	isDQPKnown: boolean;
	dqpName?: string;
	comment?: string;
}

export const deviceFaultyFormLabels: FormLabels<DeviceFaultyFormValues> = {
	deviceID: 'Select the device that is faulty or suspected of being faulty',
	faultIdentifiedDate: 'Select the date that you became aware of, or suspected, the fault',
	faultReason: 'Select the reason(s) for the fault (select all options that apply)',
	relevantAuthorisations:
		'Enter details of your other authorisations that take water through the works',
	hasOtherPersons:
		'Are any other persons taking water through the works related to this faulty device?',
	personDetails: 'Enter the person(s) name and contact details',
	isDQPKnown:
		'Do you know the name of the Duly Qualified Person (DQP) that has been, or is likely to be engaged, to certify the device?',
	dqpName: 'DQP name',
	comment: 'Comments',
};

// Validation error messages (local)
const dropdownErrorMessage = 'Please select an option';
const dateErrorMessage2 = 'Please select a date';
const multiOptionErrorMessage = 'Please select at least one option';
const optionErrorMessage2 = 'Please select an option';
const explanationErrorMessage2 = 'Please complete this field';

export const deviceFaultyFormSchema = yup.object().shape({
	deviceID: yup.string().required(dropdownErrorMessage),
	faultIdentifiedDate: yup
		.date()
		.required(dateErrorMessage2)
		.test('max-calendar-date', 'Date cannot be in the future', value => {
			const selectedDate = value;
			const maxDateValue = new Date();
			return !!selectedDate && selectedDate <= maxDateValue;
		}),
	faultReason: yup
		.array()
		.of(yup.mixed<FaultyDeviceRequestBody.faultReason>().defined())
		.required(multiOptionErrorMessage)
		.min(1, multiOptionErrorMessage),
	relevantAuthorisations: yup
		.string()
		.transform(emptyToUndefined)
		.matches(safeAnnotationTextRegExp, safeAnnotationTextErrorMessage)
		.max(textMaxLength, maxLengthErrorMsg(textMaxLength))
		.notRequired()
		.nonNullable(),
	hasOtherPersons: yup.boolean().required(optionErrorMessage2),
	personDetails: yup
		.string()
		.transform(emptyToUndefined)
		.matches(safeAnnotationTextRegExp, safeAnnotationTextErrorMessage)
		.max(textMaxLength, maxLengthErrorMsg(textMaxLength))
		.when('hasOtherPersons', {
			is: true,
			then: schema => schema.required(explanationErrorMessage2),
			otherwise: schema => schema.notRequired(),
		})
		.nonNullable(),
	isDQPKnown: yup.boolean().required(optionErrorMessage2),
	dqpName: yup
		.string()
		.transform(emptyToUndefined)
		.matches(safeAnnotationTextRegExp, safeAnnotationTextErrorMessage)
		.max(100, maxLengthErrorMsg(100))
		.when('isDQPKnown', {
			is: true,
			then: schema => schema.required(explanationErrorMessage2),
			otherwise: schema => schema.notRequired(),
		})
		.nonNullable(),
	comment: yup
		.string()
		.transform(emptyToUndefined)
		.matches(safeAnnotationTextRegExp, safeAnnotationTextErrorMessage)
		.max(textMaxLength, maxLengthErrorMsg(textMaxLength))
		.notRequired()
		.nonNullable(),
});

export const defaultDeviceFaultyFormValues: DeviceFaultyFormValues = {
	deviceID: undefined as unknown as string,
	faultIdentifiedDate: undefined as unknown as Date,
	faultReason: undefined as unknown as FaultyDeviceRequestBody.faultReason[],
	relevantAuthorisations: undefined as unknown as string,
	hasOtherPersons: undefined as unknown as boolean,
	personDetails: undefined as unknown as string,
	isDQPKnown: undefined as unknown as boolean,
	dqpName: undefined as unknown as string,
	comment: undefined as unknown as string,
};
